
import React, {
  createContext,
  useContext,
  useEffect,
  useState,
} from "react";

import { useTranslation } from "react-i18next";

const StateContext = createContext();

const initialState = {
  chat: false,
  cart: false,
  userProfile: false,
  notification: false,
};

export const ContextProvider = ({ children }) => {
  const { i18n } = useTranslation();

  /* ======================================
     LANGUAGE AND DIRECTION
     Single source of truth: i18n
  ====================================== */

  const language =
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar";

  const currentLang = language
    .toLowerCase()
    .startsWith("en")
    ? "en"
    : "ar";

  const currentDir = i18n.dir(language);

  /* ======================================
     GLOBAL DIRECTION SYNC
  ====================================== */

  useEffect(() => {
    const html = document.documentElement;

    // Set HTML language
    html.setAttribute("lang", currentLang);

    // Set HTML direction
    html.setAttribute("dir", currentDir);

    // Preserve existing storage keys
    localStorage.setItem(
      "themeLang",
      currentLang
    );

    localStorage.setItem(
      "themeDir",
      currentDir
    );
  }, [currentLang, currentDir]);

  /* ======================================
     LANGUAGE ACTIONS
  ====================================== */

  const setLang = (lang) => {
    const nextLanguage = lang
      .toLowerCase()
      .startsWith("en")
      ? "en"
      : "ar";

    return i18n.changeLanguage(nextLanguage);
  };

  // Compatibility with existing components
  const setDir = (dir) => {
    const nextLanguage =
      dir === "ltr" ? "en" : "ar";

    return setLang(nextLanguage);
  };

  /* ======================================
     THEME
  ====================================== */

  const [currentMode, setCurrentMode] =
    useState("Dark");

  const [currentColor] =
    useState("#C19B60");

  const setMode = (mode) => {
    setCurrentMode(mode);

    localStorage.setItem(
      "themeMode",
      mode
    );
  };

  /* ======================================
     UI STATES
  ====================================== */

  const [activeMenu, setActiveMenu] =
    useState(true);

  const [isClicked, setIsClicked] =
    useState(initialState);

  const [screenSize, setScreenSize] =
    useState(undefined);

  const handleClick = (clicked) => {
    setIsClicked({
      ...initialState,
      [clicked]: true,
    });
  };

  /* ======================================
     CONTEXT PROVIDER
  ====================================== */

  return (
    <StateContext.Provider
      value={{
        // Language
        currentDir,
        currentLang,
        setDir,
        setLang,

        // Backward-compatible setters
        setCurrentDir: setDir,
        setCurrentLang: setLang,

        // Theme
        currentMode,
        currentColor,
        setMode,

        // Menu
        activeMenu,
        setActiveMenu,

        // UI states
        isClicked,
        initialState,
        setIsClicked,
        handleClick,

        // Screen
        screenSize,
        setScreenSize,
      }}
    >
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () =>
  useContext(StateContext);
